import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { 
  CheckCircle, 
  Package, 
  Mail,
  Phone,
  ArrowRight,
  Loader2,
  AlertCircle
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function OrderSuccessPage() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const orderId = searchParams.get('order_id');
  
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (sessionId) {
      checkPaymentStatus();
    }
    if (orderId) {
      fetchOrder();
    }
    // Clear cart after successful order
    localStorage.removeItem('clearpath_cart');
  }, [sessionId, orderId]);

  const checkPaymentStatus = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/shop/checkout/status/${sessionId}`);
      const data = await response.json();
      setPaymentStatus(data);
    } catch (error) {
      console.error("Error checking payment status:", error);
    }
  };

  const fetchOrder = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/shop/order/${orderId}`);
      if (response.ok) {
        const data = await response.json();
        setOrder(data);
      } else {
        setError("Order not found");
      }
    } catch (error) {
      console.error("Error fetching order:", error);
      setError("Failed to load order details");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 text-sky-600 animate-spin mx-auto mb-4" />
          <p className="text-slate-600">Confirming your order...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center max-w-md">
          <AlertCircle className="w-16 h-16 text-amber-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Order Status Pending</h1>
          <p className="text-slate-600 mb-6">
            We're processing your order. If you completed payment, you should receive a confirmation email shortly.
          </p>
          <div className="space-y-3">
            <Link to="/shop">
              <Button className="w-full bg-sky-600 hover:bg-sky-700 text-white">
                Return to Shop
              </Button>
            </Link>
            <p className="text-sm text-slate-500">
              Questions? Contact <a href="mailto:sales@clearpathrapid.com" className="text-sky-600">sales@clearpathrapid.com</a>
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-slate-50 min-h-screen">
      {/* Success Header */}
      <section className="bg-white border-b border-slate-200">
        <div className="max-w-3xl mx-auto px-6 py-12 text-center">
          <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-emerald-600" />
          </div>
          <h1 className="text-3xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="success-headline">
            Order Confirmed!
          </h1>
          <p className="text-lg text-slate-600 mb-2">
            Thank you for your order, {order?.customer?.full_name}
          </p>
          <p className="text-sm text-slate-500">
            Order #{orderId}
          </p>
        </div>
      </section>

      {/* Order Details */}
      <div className="max-w-3xl mx-auto px-6 py-8">
        {/* Next Steps */}
        <div className="bg-sky-50 border border-sky-200 rounded-xl p-6 mb-6">
          <h2 className="text-lg font-semibold text-sky-900 mb-3 flex items-center gap-2">
            <Package className="w-5 h-5" />
            What Happens Next
          </h2>
          <ul className="space-y-2 text-sky-800">
            <li className="flex items-start gap-2">
              <span className="w-5 h-5 bg-sky-200 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 text-xs font-bold">1</span>
              <span>You'll receive an order confirmation email shortly.</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="w-5 h-5 bg-sky-200 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 text-xs font-bold">2</span>
              <span>Our team will review your order and confirm availability.</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="w-5 h-5 bg-sky-200 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 text-xs font-bold">3</span>
              <span>ClearPath Rapid Diagnostics will follow up with shipping and fulfillment details.</span>
            </li>
          </ul>
        </div>

        {/* Order Summary */}
        {order && (
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden mb-6">
            <div className="px-6 py-4 border-b border-slate-200 bg-slate-50">
              <h2 className="font-semibold text-slate-900">Order Summary</h2>
            </div>
            <div className="p-6">
              {/* Items */}
              <div className="space-y-3 mb-6">
                {order.items?.map((item, index) => (
                  <div key={index} className="flex justify-between items-center py-2 border-b border-slate-100">
                    <div>
                      <p className="font-medium text-slate-900">{item.product_name}</p>
                      <p className="text-sm text-slate-500">Qty: {item.quantity} × ${item.unit_price?.toFixed(2)}</p>
                    </div>
                    <p className="font-medium text-slate-900">${item.subtotal?.toFixed(2)}</p>
                  </div>
                ))}
              </div>
              
              {/* Total */}
              <div className="flex justify-between items-center pt-4 border-t border-slate-200">
                <span className="text-lg font-bold text-slate-900">Total Paid</span>
                <span className="text-xl font-bold text-emerald-600">${order.total?.toFixed(2)}</span>
              </div>
            </div>
          </div>
        )}

        {/* Shipping Info */}
        {order?.customer && (
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden mb-6">
            <div className="px-6 py-4 border-b border-slate-200 bg-slate-50">
              <h2 className="font-semibold text-slate-900">Shipping Information</h2>
            </div>
            <div className="p-6">
              <p className="text-slate-900 font-medium">{order.customer.company_name}</p>
              <p className="text-slate-600">{order.customer.full_name}</p>
              <p className="text-slate-600">{order.customer.shipping_address}</p>
              <p className="text-slate-600">
                {order.customer.shipping_city}, {order.customer.shipping_state} {order.customer.shipping_zip}
              </p>
            </div>
          </div>
        )}

        {/* Contact Info */}
        <div className="bg-white rounded-xl border border-slate-200 p-6 mb-6">
          <h2 className="font-semibold text-slate-900 mb-4">Questions About Your Order?</h2>
          <div className="flex flex-col sm:flex-row gap-4">
            <a 
              href="mailto:sales@clearpathrapid.com" 
              className="flex items-center gap-2 text-sky-600 hover:text-sky-700"
            >
              <Mail className="w-4 h-4" />
              sales@clearpathrapid.com
            </a>
            <a 
              href="tel:817-508-6232" 
              className="flex items-center gap-2 text-sky-600 hover:text-sky-700"
            >
              <Phone className="w-4 h-4" />
              (817) 508-6232
            </a>
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link to="/shop">
            <Button variant="outline" className="w-full sm:w-auto border-slate-300">
              Continue Shopping
            </Button>
          </Link>
          <Link to="/">
            <Button className="w-full sm:w-auto bg-sky-600 hover:bg-sky-700 text-white">
              Return to Home
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
